import network from '../network/network';
import { getDomain, isBlog, resolveCountryCode } from './domain';

import {
  isDebugTopAd,
  clone,
} from '../helpers/utils';
import amaLog, { amaWarn } from '../helpers/log';
import { getCookie, setCookie } from '../storage/cookies';

let savedAccConfig: IAdsConfig | undefined;
const highestIds: HighestIds = {
  responsive: 0,
  intext_ad: 0,
};
export const getSlotVerticalIndex = (domId: string, pageNo: number): number => {
  const currentEl = document.getElementById(domId);
  if (!currentEl) return 1;

  const pageSuffix = `-${pageNo}`;

  const allAdElements = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-aller-ad-placement], [id*="responsive_"], [id*="intext_ad_"], [id*="front_split"], [id*="ad_content_"]'
    )
  ).filter((el) => {
    const id = el.id || '';
    const placement = el.getAttribute('data-aller-ad-placement') || '';

    if (id.includes('top_ad') || placement.includes('top_ad')) {
      return false;
    }

    return id.endsWith(pageSuffix);
  });

  allAdElements.sort((a, b) => {
    const topA = a.getBoundingClientRect().top + window.scrollY;
    const topB = b.getBoundingClientRect().top + window.scrollY;
    return topA - topB;
  });

  const index = allAdElements.indexOf(currentEl);
  return index !== -1 ? index + 1 : 1;
};


export const modifyMainCategoryPlacementsDivs = (processedDivIds: Set<string>): void => {
  document.querySelectorAll<HTMLElement>("[id^='responsive_'], [id^='intext_ad_']").forEach((div) => {
    if (processedDivIds.has(div.id)) return;

    const match = div.id.match(/(responsive|intext_ad)_(\d+)(-.+)?/);
    if (!match) return;

    const [_, type, , suffix = ''] = match;

    highestIds[type] += 1;
    const newId = `${type}_${highestIds[type]}${suffix}`;

    div.id = newId;
    div.setAttribute('data-aller-ad-placement', highestIds[type] >= 9 ? `${type}_X` : newId.split('-')[0]);
    processedDivIds.add(div.id);
  });
};

export const withinTopScrollerLimit = (
  increment: boolean = false
): boolean => {
  const config = window.ama?.topAdFrequency || { hours: 1, adLimit: 3 };

  const cookieName = 'aller_top_ad';
  const now = Date.now();
  const windowMs = config.hours * 60 * 60 * 1000;

  let cookieData: { start: number; count: number } = {
    start: now,
    count: 0
  };

  try {
    const existing = getCookie(cookieName);
    if (existing) {
      const parsed =
        typeof existing === 'string' ? JSON.parse(existing) : existing;

      if (parsed.start && now - parsed.start < windowMs) {
        cookieData = parsed;
      }
    }
  } catch (err) {
    amaLog('Error parsing hourly cookie data:', err);
  }

  const withinLimit = cookieData.count < config.adLimit;

  if (increment && withinLimit) {
    cookieData.count += 1;
    setCookie(cookieName, JSON.stringify(cookieData));
  }
  amaLog(`Show top ad. withinLimit: ${withinLimit}, config.hours: ${config.hours},config.adLimit: ${config.adLimit}, cookieData.count, ${cookieData.count} ads. `);

  return withinLimit;
};


const modifyArticlesPlacementsDivs = (divId: string, placement: string) => {
  let placementDivs = document.getElementById(divId);
  if (placementDivs) {
    placementDivs.setAttribute('data-aller-ad-placement', placement);
  }
};

const fetchAdsConfig = async () => {
  const host = `${ isBlog() ? 'blog.':''}${getDomain()}`;
  let sp = '';
/////////////////////////////////////////////////////////////////////////////////////////
  sp = 'sp-';
///////////
  const configUrl = `${process.env.FALLBACK_CONFIG_BASE_URL}${sp}${host}.json`;
  try {
    return await network.get(configUrl);
  } catch (err) {
    amaWarn(`Failed fetch config for ${configUrl}`);
  }
};

const getModifiedConfig = async (serverLoadedConfig?: IAdsConfig) => {
  savedAccConfig =
    savedAccConfig ||
    (serverLoadedConfig && Object.keys(serverLoadedConfig).length > 0 ? serverLoadedConfig : await getConfig());

  amaLog(`ACC Config ${!!serverLoadedConfig ? "(serverLoadedConfig)" : ''}:`, savedAccConfig);

  if (!(savedAccConfig?.ads && savedAccConfig.ads.length > 0)) {
    throw new Error('Error ACC config is missing');
  }

  return savedAccConfig && clone(savedAccConfig);
};

const getConfig = async () => {
  try {
    return await fetchAdsConfig();
  } catch (e) {
    console.error('Failed to retrieve ads configuration.');
  }
};

// With this function and defining placements in ACC it can cover sizes config that are not defined in ACC but exists on the page,
// For example if acc configs are ad-banner1 (3 sizes), ad-banner4 (1 size) and ad-bannerx(2 sizes). 
// ad-bannerx would be used for all placements from 2 or higher but not 4, cause it is defined explitly in acc config.
// So to cover all placements set the first placement ([placementName]1) for each type and another placement x ([placementName]x) for that type.
export const findAdConfigForPlacement = (config: any, divId: string) => {

  if (!config?.ads?.length || !divId) return;

  const ads = [ ...config.ads ];
  const lowerDivId = divId.toLowerCase();
  // 1. Try to find match for the divId in the accConfig, and return if found
  // (divId may equal target exactly when the pageNo/counter suffix is skipped for the first occurrence, see useSkipPageNoSuffix)
  let foundAccConfigTarget = ads.find(
    ({ target }: any) => lowerDivId.includes(`${target}-`) || lowerDivId === `${target}`.toLowerCase(),
  );
  
  if (foundAccConfigTarget) {
    amaLog('foundAccConfigTarget for divId:', divId);
    return foundAccConfigTarget;
  } 

  // Strip a trailing "-<counter>" suffix if present; a bare divId (see useSkipPageNoSuffix) has none,
  // and must be used as-is so placement names with an internal hyphen (e.g. "ad-netboard2") aren't truncated.
  const counterSuffixMatch = lowerDivId.match(/-(\d+)$/);
  const placement = counterSuffixMatch ? lowerDivId.slice(0, counterSuffixMatch.index) : lowerDivId;

  const lastNumbers = parseInt(placement.match(/\d+$/)?.[0] ?? '', 10);
  const numberThreshold = resolveCountryCode() === 'no' ? 2 : 9;

  // 2 If the placement ends with a number, check if it's within the threshold. Then use x-placement if found in next step
  if (!lastNumbers || lastNumbers < numberThreshold) return;

  const xPlacementToMatch = placement.replace(/\d+$/, 'x');

  // 3. Look for x-placement for corresponding placement
  const xMatchingConfig = ads.find(({ target }: any) => `${target || ''}`.toLowerCase() === xPlacementToMatch);

  if (!xMatchingConfig) {
    amaWarn(`No matching x-placement from acc config found for divId ${divId}`);
    return;
  }
  // 4. If x-placement config found, modify target property to be the same as divId
  //  If NO, also modify placement property to reflect the divId, it will be used as keyValue "pos" later.
  let newPlacement = resolveCountryCode() !== 'no' ? xMatchingConfig.placement : placement;

  foundAccConfigTarget = {
    ...xMatchingConfig,
    target: divId,
    placement: newPlacement, 
  };
  amaLog('Resolved extra placement config for divId:', divId );
  return foundAccConfigTarget;
};

const addExtraPlacementsToConfig = (config: any, placement: string) => {
  amaLog('addExtraPlacements if needed ', placement);

  const ads = [...config.ads];
  placement = `${placement}${placement.includes('intext') ? '_ad' : ''}`;
  const xSlot = ads.find((adSlot: any) => {
    const target = adSlot.target;
    return target.includes(`${placement}`) && (target.includes('-x') || target.includes('_x'));
  });

  const placementDivs: any[] | NodeListOf<Element> = placement
    ? document.querySelectorAll(`[id^="${placement}"]`) || []
    : [];

  if (xSlot && placementDivs.length > 0) {
    for (let no = 0; no < placementDivs.length; no++) {
      const target = placementDivs[no].id;
      const newTarget: String = target && target.includes(placement) && target.substring(0, target.indexOf('-'));
      const slotExists = ads.some((adSlot: any) => adSlot.target === `${newTarget}`);

      if (slotExists) {
        // Slot already exists. Will not add;
        continue;
      }
      const newPlacement = `${placement}_X`;

      if (newTarget) {
        amaLog(`Adding target ${newTarget} and placement ${newPlacement} from ${target}. div-id: ${placementDivs[no].id}`);

        ads.push({
          ...xSlot,
          target: newTarget,
          placement: newPlacement,
        });
      } else {
        amaLog(`Couldn't set intext_ad_X for ${target}, on index ${no + 1}`);
      }
    }
  } else {
    amaLog(`${placementDivs.length} ${placement} found, so no ${placement}_X will be set`);
  }
  amaLog('ads after adding extra:', ads);

  return { ...config, ads };
};

const loadAccConfig = async (serverLoadedConfig?: IAdsConfig) => {

  amaLog(`loadAccConfig serverLoadedConfig:`, typeof serverLoadedConfig);

  const savedAccConfig = typeof serverLoadedConfig === 'object' && Object.keys(serverLoadedConfig).length > 0 ? serverLoadedConfig : await getConfig();

  amaLog(`ACC Config ${!!serverLoadedConfig ? "(serverLoadedConfig)" : ''}:`, savedAccConfig);

  if (!(savedAccConfig?.ads && savedAccConfig.ads.length > 0)) {
    throw new Error('Error ACC config is missing');
  }

  return savedAccConfig;
};


const adConfig = {
  get: getConfig,
  getModifiedConfig: getModifiedConfig,
  loadAccConfig,
  addExtraPlacementsToConfig,
  findAdConfigForPlacement,
};

export default adConfig;
